import React from 'react';

import { Button, formatNumber } from '@column/column-ui-kit';
import { Badge } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { Section } from '~/components';
import { useCanManageCardDisputes, useCardDisputes } from '~/lib/api/cardDisputes';
import {
  getDisputeReasonLabel,
  getSubmissionStatusBadgeClasses,
  getSubmissionStatusLabel,
} from '~/lib/disputes/labels';
import { useNavigate } from '~/lib/navigation';

import type { CardTransactionProps } from './CardTransaction';

/**
 * Part 04 initiation entry point: managers can start a dispute from a settled transaction, and
 * every reader sees links to the transaction's existing disputes.
 */
export const DisputeTransactionEntry: React.FC<{ transaction: CardTransactionProps['cardTransaction'] }> = ({
  transaction,
}) => {
  const navigate = useNavigate();
  const canManage = useCanManageCardDisputes();
  const { cardDisputes } = useCardDisputes({ query: { card_transaction_id: transaction.id } });

  const settled = transaction.totalAmounts?.cardholderSettled;
  // A settled debit stores a negative cardholder amount; credits and pending authorizations are
  // not disputable, and the wizard's eligibility check reports that to the backend's standard.
  const isSettledDebit = settled !== undefined && settled.amount < 0;

  return (
    <Section headline="Disputes">
      <div style={{ padding: '0 var(--page-margin) 16px' }} data-testid="dispute-transaction-entry">
        {canManage && isSettledDebit && (
          <Button
            variant="secondary"
            size="small"
            onClick={() => navigate(`${ROUTE.CARD_DISPUTES_NEW}?transaction_id=${transaction.id}`)}
            data-testid="dispute-transaction-start"
          >
            Dispute transaction
          </Button>
        )}
        {cardDisputes.length > 0 ? (
          <ul className="flex flex-col gap-2" data-testid="dispute-transaction-links">
            {cardDisputes.map((dispute) => (
              <li key={dispute.id} className="flex flex-wrap items-center gap-2 text-sm">
                <a
                  className="font-mono text-xs text-primary underline-offset-4 hover:underline"
                  href={`${ROUTE.CARD_DISPUTES}/edit/${dispute.id}`}
                >
                  {dispute.id}
                </a>
                <span>{getDisputeReasonLabel(dispute.reason)}</span>
                <span>{formatNumber(dispute.amount, dispute.currency)}</span>
                <Badge variant="outline" className={getSubmissionStatusBadgeClasses(dispute.submission_status)}>
                  {getSubmissionStatusLabel(dispute.submission_status)}
                </Badge>
              </li>
            ))}
          </ul>
        ) : (
          !canManage && <p className="text-sm text-muted-foreground">No disputes for this transaction.</p>
        )}
      </div>
    </Section>
  );
};
